import React, { useEffect, useMemo, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import {
  getPendingOrdersEmailSettings,
  updatePendingOrdersEmailSettings,
  sendPendingOrdersTestEmail,
  listPendingOrdersEmailLog,
} from "../services/pendingOrdersEmailApi";

const SETTINGS_DOC = () => doc(db, "settings", "pendingOrdersEmail");

const SMTP_PRESETS = [
  { id: "gmail", label: "Gmail", host: "smtp.gmail.com", port: 465, secure: true, hint: "Use a 16-character App password." },
  { id: "outlook", label: "Microsoft 365", host: "smtp.office365.com", port: 587, secure: false, hint: "Port 587, SSL/TLS off (STARTTLS)." },
  { id: "zoho", label: "Zoho Mail", host: "smtp.zoho.in", port: 465, secure: true, hint: "Use smtp.zoho.com if your account is not India." },
];

const inputCls =
  "mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-violet-400 focus:ring-2 focus:ring-violet-500/20";

function defaultSettings() {
  return {
    enabled: false,
    smtpHost: "",
    smtpPort: 465,
    smtpSecure: true,
    smtpUser: "",
    smtpPass: "",
    fromName: "PRS.NET ERP",
    fromEmail: "",
    appBaseUrl: "",
    notifyPartyOnOrderSaveAttachment: true,
    emailSubjectPrefix: "Spare Parts Order",
    emailCompanyDisplayName: "Wins Autos",
    emailGreeting: "SIR,",
    emailAttachmentIntro: "Please Find The Attachment",
    emailAttachmentIntroPlural: "Please Find The Attachments",
    emailNoFilesIntro: "Please Find The Attachment",
    emailRegardsName: "PRANEESH",
    emailSignatureBanner: "WINS AUTOS",
    emailSignatureHtml: "",
    bccEmail: "",
  };
}

function normalizeForForm(raw) {
  const base = { ...defaultSettings(), ...(raw || {}) };
  const pass = String(base.smtpPass || "").trim();
  if (pass && pass !== "********") base.smtpPass = "********";
  return base;
}

function describeSkippedTestEmail(reason, opts = {}) {
  const localEnabled = !!opts.localEnabled;
  const r = String(reason || "").trim().toLowerCase();
  if (r === "disabled") {
    if (localEnabled) {
      return "Save settings first — Enable sending is not active on the server yet.";
    }
    return "Sending is off on the server. Check Enable sending, click Save settings, then try again.";
  }
  if (r === "no_smtp") {
    const labels = { host: "SMTP host", user: "SMTP user", password: "SMTP password" };
    const parts = (Array.isArray(opts.missing) ? opts.missing : [])
      .map((k) => labels[k] || k)
      .filter(Boolean);
    if (parts.length) {
      return `On the server, these are still empty: ${parts.join(", ")}. Fill them in, click Save settings, then try again. If only "password" is listed, type your SMTP password and save (the form hides the saved value).`;
    }
    return "SMTP is incomplete. Set host, user, and password, save, then try again.";
  }
  return reason ? `Not sent: ${reason}` : "Test email was not sent.";
}

function buildPreviewSubject(cfg) {
  const prefix = String(cfg?.emailSubjectPrefix || "Spare Parts Order").trim() || "Spare Parts Order";
  const company = String(cfg?.emailCompanyDisplayName || "Wins Autos").trim() || "Wins Autos";
  return `${prefix} - ${company} - PO#12345`;
}

function buildPreviewBody(cfg, mode = "single") {
  const greeting = String(cfg?.emailGreeting || "SIR,").trim() || "SIR,";
  let intro;
  if (mode === "none") intro = String(cfg?.emailNoFilesIntro || "Please Find The Attachment").trim();
  else if (mode === "plural") intro = String(cfg?.emailAttachmentIntroPlural || "Please Find The Attachments").trim();
  else intro = String(cfg?.emailAttachmentIntro || "Please Find The Attachment").trim();
  const regards = String(cfg?.emailRegardsName || "PRANEESH").trim() || "PRANEESH";
  const banner = String(cfg?.emailSignatureBanner || "WINS AUTOS").trim() || "WINS AUTOS";
  const customSig = String(cfg?.emailSignatureHtml || "").trim();
  return { greeting, intro, regards, banner, customSig };
}

function Field({ label, hint, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs font-semibold text-slate-700">{label}</label>
      {children}
      {hint ? <p className="mt-1 text-[11px] leading-snug text-slate-500">{hint}</p> : null}
    </div>
  );
}

function StatusPill({ ok, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
        ok ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200" : "bg-slate-100 text-slate-600 ring-1 ring-slate-200"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-slate-400"}`} />
      {label}
    </span>
  );
}

function EmailPreview({ cfg, previewMode }) {
  const body = buildPreviewBody(cfg, previewMode);
  const from = String(cfg?.fromEmail || cfg?.smtpUser || "you@company.com").trim();
  const fromName = String(cfg?.fromName || "PRS.NET ERP").trim();

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Live preview</p>
      <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="border-b border-slate-100 pb-3 text-xs text-slate-600">
          <div className="truncate">
            <span className="font-semibold text-slate-800">Subject:</span> {buildPreviewSubject(cfg)}
          </div>
          <div className="mt-1 truncate">
            <span className="font-semibold text-slate-800">From:</span> {fromName} &lt;{from}&gt;
          </div>
        </div>
        <div className="pt-3 font-sans text-sm leading-relaxed text-slate-800">
          <p className="mb-3">{body.greeting}</p>
          <p className="mb-3">{body.intro}</p>
          <p className="mb-1">Regards</p>
          <p className="mb-4 font-medium">{body.regards}</p>
          <hr className="border-slate-200" />
          {body.customSig ? (
            <p className="mt-3 text-[11px] text-slate-500">Custom signature HTML will be used.</p>
          ) : (
            <div className="mt-4 text-center">
              <span className="inline-block rounded-lg bg-gradient-to-br from-rose-500 to-red-700 px-6 py-2 text-sm font-bold italic tracking-widest text-white shadow-md">
                {body.banner}
              </span>
              <p className="mt-3 text-[10px] uppercase leading-relaxed text-slate-600">
                Wins Tower · Calicut · GST · Contact block (built-in)
              </p>
            </div>
          )}
        </div>
      </div>
      <p className="mt-2 text-[11px] text-slate-500">PO number in the real email uses the order SL field.</p>
    </div>
  );
}

export default function PendingOrderEmailSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [savedSnapshot, setSavedSnapshot] = useState("");
  const [smtpPassInput, setSmtpPassInput] = useState("");
  const [testTo, setTestTo] = useState("");
  const [viaFirestore, setViaFirestore] = useState(false);
  const [gmailAppPasswordHelp, setGmailAppPasswordHelp] = useState(false);
  const [previewMode, setPreviewMode] = useState("single");
  const [activePreset, setActivePreset] = useState("");
  const [sendLog, setSendLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const userEmail = String(auth.currentUser?.email || "").trim();

  const loadSendLog = async () => {
    if (viaFirestore) return;
    setLogLoading(true);
    try {
      const res = await listPendingOrdersEmailLog({ limit: 25 });
      setSendLog(res?.data?.entries || []);
    } catch (e) {
      console.warn("listPendingOrdersEmailLog", e);
    } finally {
      setLogLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setViaFirestore(false);
      try {
        try {
          const res = await getPendingOrdersEmailSettings();
          if (!cancelled && res?.data?.settings) {
            const n = normalizeForForm(res.data.settings);
            setCfg(n);
            setSavedSnapshot(JSON.stringify(n));
            if (!cancelled) setTimeout(() => loadSendLog(), 0);
            return;
          }
        } catch (e) {
          console.warn("getPendingOrdersEmailSettings", e);
        }
        try {
          const snap = await getDoc(SETTINGS_DOC());
          if (!cancelled) {
            setViaFirestore(true);
            const n = normalizeForForm(snap.exists() ? snap.data() : {});
            setCfg(n);
            setSavedSnapshot(JSON.stringify(n));
          }
        } catch (e2) {
          if (!cancelled) {
            const n = normalizeForForm({});
            setCfg(n);
            setSavedSnapshot(JSON.stringify(n));
            toast.error(`Could not load settings: ${e2?.message || e2}`);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const dirty = useMemo(() => {
    if (!cfg || !savedSnapshot) return false;
    const passDirty = !!smtpPassInput.trim();
    try {
      return passDirty || JSON.stringify(cfg) !== savedSnapshot;
    } catch {
      return passDirty;
    }
  }, [cfg, savedSnapshot, smtpPassInput]);

  const smtpReady = useMemo(() => {
    if (!cfg) return false;
    const host = String(cfg.smtpHost || "").trim();
    const user = String(cfg.smtpUser || "").trim();
    const hasPass = cfg.smtpPass === "********" || !!smtpPassInput.trim();
    return !!(host && user && hasPass);
  }, [cfg, smtpPassInput]);

  const applyPreset = (preset) => {
    setActivePreset(preset.id);
    setCfg((c) => ({
      ...c,
      smtpHost: preset.host,
      smtpPort: preset.port,
      smtpSecure: preset.secure,
    }));
    toast.success(`${preset.label} SMTP values applied — enter user/password and save.`, { duration: 4000 });
  };

  const save = async () => {
    if (!cfg) return;
    if (cfg.enabled && !smtpReady) {
      return toast.error("Enable sending requires SMTP host, user, and password.");
    }
    setSaving(true);
    try {
      const patch = { ...cfg };
      delete patch.smtpPass;
      if (smtpPassInput.trim()) {
        let p = smtpPassInput.trim();
        if (String(cfg.smtpHost || "").toLowerCase().includes("gmail")) {
          p = p.replace(/\s/g, "");
        }
        patch.smtpPass = p;
      }

      try {
        const res = await updatePendingOrdersEmailSettings({ settings: patch });
        if (res?.data?.settings) {
          const n = normalizeForForm(res.data.settings);
          setCfg(n);
          setSavedSnapshot(JSON.stringify(n));
        }
        setViaFirestore(false);
        toast.success("Settings saved");
        loadSendLog();
      } catch (e) {
        console.warn("updatePendingOrdersEmailSettings", e);
        await setDoc(SETTINGS_DOC(), { ...cfg, ...patch }, { merge: true });
        const snap = await getDoc(SETTINGS_DOC());
        if (snap.exists()) {
          const n = normalizeForForm(snap.data());
          setCfg(n);
          setSavedSnapshot(JSON.stringify(n));
        }
        setViaFirestore(true);
        toast.success("Saved to Firestore (deploy functions for full features)");
      }
      setSmtpPassInput("");
    } catch (e) {
      toast.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (viaFirestore) {
      return toast.error("Deploy Cloud Functions (firebase deploy --only functions) to send test email.");
    }
    const to = String(testTo || userEmail || "").trim();
    if (!to) return toast.error("Enter test recipient email");
    if (dirty) return toast.error("Save settings before sending a test email.");
    setTesting(true);
    try {
      const res = await sendPendingOrdersTestEmail({ to });
      if (res?.data?.skipped) {
        const msg = describeSkippedTestEmail(res.data.reason, {
          localEnabled: !!cfg?.enabled,
          missing: res.data.missing,
        });
        toast(msg, { icon: "ℹ️", duration: 8000 });
      } else {
        toast.success(res?.data?.subject ? `Test sent — subject: ${res.data.subject}` : "Test email sent");
        setGmailAppPasswordHelp(false);
        loadSendLog();
      }
    } catch (e) {
      const msg = e?.message || "Send failed";
      const low = msg.toLowerCase();
      if (
        low.includes("534") ||
        low.includes("application-specific password") ||
        low.includes("invalidsecondfactor") ||
        low.includes("16 letters") ||
        low.includes("apppasswords")
      ) {
        setGmailAppPasswordHelp(true);
      }
      if (String(e?.code || "").includes("failed-precondition")) {
        toast.error(msg, { duration: 12000 });
      } else {
        toast.error(msg);
      }
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-violet-50/30">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">Pending order email</h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              Sends supplier emails when you submit a pending order with <strong>Order type = Email</strong>. Files are
              attached when uploaded. In <strong>Party master</strong>, you can enter <strong>multiple emails</strong>{" "}
              separated by commas — all receive the same message.
            </p>
          </div>
          {!loading && cfg ? (
            <div className="flex flex-wrap gap-2">
              <StatusPill ok={!!cfg.enabled} label={cfg.enabled ? "Sending enabled" : "Sending off"} />
              <StatusPill ok={smtpReady} label={smtpReady ? "SMTP configured" : "SMTP incomplete"} />
              <StatusPill ok={!viaFirestore} label={viaFirestore ? "Firestore only" : "Cloud Functions"} />
            </div>
          ) : null}
        </div>

        {viaFirestore ? (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            Settings are read/written in Firestore until Cloud Functions are deployed. Test email requires{" "}
            <code className="rounded bg-white px-1.5 py-0.5 text-xs">firebase deploy --only functions</code>.
          </div>
        ) : null}

        {dirty ? (
          <div className="mt-4 rounded-xl border border-violet-200 bg-violet-50 px-4 py-2 text-sm text-violet-900">
            You have unsaved changes.
          </div>
        ) : null}

        {loading || !cfg ? (
          <p className="mt-8 text-sm text-slate-500">Loading settings…</p>
        ) : (
          <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_320px]">
            <div className="space-y-5">
              {/* General */}
              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-sm font-bold text-slate-900">General</h2>
                <div className="mt-4 space-y-3">
                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 bg-slate-50/50 p-3">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={!!cfg.enabled}
                      onChange={(e) => setCfg((c) => ({ ...c, enabled: e.target.checked }))}
                    />
                    <span>
                      <span className="text-sm font-semibold text-slate-900">Enable sending</span>
                      <span className="mt-0.5 block text-xs text-slate-500">Required for test email and supplier notifications.</span>
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-100 p-3">
                    <input
                      type="checkbox"
                      checked={cfg.notifyPartyOnOrderSaveAttachment !== false}
                      onChange={(e) => setCfg((c) => ({ ...c, notifyPartyOnOrderSaveAttachment: e.target.checked }))}
                    />
                    <span className="text-sm text-slate-800">
                      Email supplier on submit (Order type Email, with attachments when uploaded)
                    </span>
                  </label>
                </div>
              </section>

              {/* SMTP */}
              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-sm font-bold text-slate-900">SMTP server</h2>
                <p className="mt-1 text-xs text-slate-500">Quick-fill common providers, then enter your mailbox credentials.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {SMTP_PRESETS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => applyPreset(p)}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                        activePreset === p.id
                          ? "border-violet-400 bg-violet-50 text-violet-900"
                          : "border-slate-200 bg-white text-slate-700 hover:border-violet-300 hover:bg-violet-50/50"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                {activePreset ? (
                  <p className="mt-2 text-[11px] text-violet-800">{SMTP_PRESETS.find((p) => p.id === activePreset)?.hint}</p>
                ) : null}

                <details className="mt-4 rounded-lg border border-sky-100 bg-sky-50/60 px-3 py-2 text-[11px] text-slate-700">
                  <summary className="cursor-pointer font-semibold text-sky-950">New to SMTP? Field guide</summary>
                  <ul className="mt-2 list-disc space-y-1 pl-4">
                    <li>
                      <strong>Host</strong> — e.g. <code className="rounded bg-white px-1">smtp.gmail.com</code>
                    </li>
                    <li>
                      <strong>User</strong> — usually your full email address
                    </li>
                    <li>
                      <strong>Password</strong> — Gmail needs a 16-character App password, not your login password
                    </li>
                    <li>
                      <strong>From email</strong> — what suppliers see; usually same as SMTP user
                    </li>
                  </ul>
                </details>

                {gmailAppPasswordHelp ? (
                  <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-3 text-[11px] text-rose-950">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold">Gmail error 534 — use an App password</p>
                      <button type="button" className="text-rose-800 underline" onClick={() => setGmailAppPasswordHelp(false)}>
                        Dismiss
                      </button>
                    </div>
                    <ol className="mt-2 list-decimal space-y-1 pl-4">
                      <li>Google Account → Security → 2-Step Verification</li>
                      <li>
                        Create an{" "}
                        <a className="underline" href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noreferrer">
                          App password
                        </a>
                      </li>
                      <li>Paste into SMTP password, Save, Send test</li>
                    </ol>
                  </div>
                ) : null}

                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <Field label="SMTP host" className="sm:col-span-2">
                    <input className={inputCls} value={cfg.smtpHost || ""} onChange={(e) => setCfg((c) => ({ ...c, smtpHost: e.target.value }))} placeholder="smtp.gmail.com" />
                  </Field>
                  <Field label="Port">
                    <input type="number" className={inputCls} value={cfg.smtpPort ?? 465} onChange={(e) => setCfg((c) => ({ ...c, smtpPort: Number(e.target.value) || 465 }))} />
                  </Field>
                  <label className="flex items-end gap-2 pb-2 text-sm text-slate-700">
                    <input type="checkbox" checked={cfg.smtpSecure !== false} onChange={(e) => setCfg((c) => ({ ...c, smtpSecure: e.target.checked }))} />
                    SSL/TLS (port 465)
                  </label>
                  <Field label="SMTP user">
                    <input className={inputCls} value={cfg.smtpUser || ""} onChange={(e) => setCfg((c) => ({ ...c, smtpUser: e.target.value }))} placeholder="you@company.com" />
                  </Field>
                  <Field label={`SMTP password ${cfg.smtpPass === "********" ? "(saved)" : ""}`} hint="Leave blank to keep existing. Required for first setup.">
                    <input type="password" className={inputCls} value={smtpPassInput} onChange={(e) => setSmtpPassInput(e.target.value)} placeholder="App password or SMTP password" autoComplete="new-password" />
                  </Field>
                  <Field label="From name">
                    <input className={inputCls} value={cfg.fromName || ""} onChange={(e) => setCfg((c) => ({ ...c, fromName: e.target.value }))} />
                  </Field>
                  <Field label="From email" hint="Shown to suppliers; match SMTP user unless IT says otherwise.">
                    <input type="email" className={inputCls} value={cfg.fromEmail || ""} onChange={(e) => setCfg((c) => ({ ...c, fromEmail: e.target.value }))} placeholder={cfg.smtpUser || "you@company.com"} />
                  </Field>
                  <Field label="BCC (optional)" className="sm:col-span-2" hint="Comma-separated addresses copied on every supplier and test email.">
                    <input
                      type="text"
                      className={inputCls}
                      value={cfg.bccEmail || ""}
                      onChange={(e) => setCfg((c) => ({ ...c, bccEmail: e.target.value }))}
                      placeholder="manager@company.com, accounts@company.com"
                    />
                  </Field>
                  <Field label="App link (optional)" className="sm:col-span-2">
                    <input className={inputCls} value={cfg.appBaseUrl || ""} onChange={(e) => setCfg((c) => ({ ...c, appBaseUrl: e.target.value }))} placeholder="https://your-app.web.app" />
                  </Field>
                </div>
              </section>

              {/* Wording */}
              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-sm font-bold text-slate-900">Email wording</h2>
                <p className="mt-1 text-xs text-slate-500">
                  Subject: <code className="rounded bg-slate-100 px-1">{`{prefix} - {company} - PO#{slNo}`}</code>
                </p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <Field label="Subject prefix">
                    <input className={inputCls} value={cfg.emailSubjectPrefix || ""} onChange={(e) => setCfg((c) => ({ ...c, emailSubjectPrefix: e.target.value }))} />
                  </Field>
                  <Field label="Company name (in subject)">
                    <input className={inputCls} value={cfg.emailCompanyDisplayName || ""} onChange={(e) => setCfg((c) => ({ ...c, emailCompanyDisplayName: e.target.value }))} />
                  </Field>
                  <Field label="Greeting">
                    <input className={inputCls} value={cfg.emailGreeting || ""} onChange={(e) => setCfg((c) => ({ ...c, emailGreeting: e.target.value }))} />
                  </Field>
                  <Field label="Regards / name">
                    <input className={inputCls} value={cfg.emailRegardsName || ""} onChange={(e) => setCfg((c) => ({ ...c, emailRegardsName: e.target.value }))} />
                  </Field>
                  <Field label="Body (one attachment)" className="sm:col-span-2">
                    <input className={inputCls} value={cfg.emailAttachmentIntro || ""} onChange={(e) => setCfg((c) => ({ ...c, emailAttachmentIntro: e.target.value }))} />
                  </Field>
                  <Field label="Body (multiple attachments)" className="sm:col-span-2">
                    <input className={inputCls} value={cfg.emailAttachmentIntroPlural || ""} onChange={(e) => setCfg((c) => ({ ...c, emailAttachmentIntroPlural: e.target.value }))} />
                  </Field>
                  <Field label="Body (no files)" className="sm:col-span-2">
                    <input className={inputCls} value={cfg.emailNoFilesIntro || ""} onChange={(e) => setCfg((c) => ({ ...c, emailNoFilesIntro: e.target.value }))} />
                  </Field>
                  <Field label="Signature banner">
                    <input className={inputCls} value={cfg.emailSignatureBanner || ""} onChange={(e) => setCfg((c) => ({ ...c, emailSignatureBanner: e.target.value }))} />
                  </Field>
                  <Field label="Custom signature HTML" className="sm:col-span-2" hint="Replaces the built-in Wins Autos block when set.">
                    <textarea rows={4} className={`${inputCls} font-mono text-xs`} value={cfg.emailSignatureHtml || ""} onChange={(e) => setCfg((c) => ({ ...c, emailSignatureHtml: e.target.value }))} />
                  </Field>
                </div>
              </section>

              {/* Send log */}
              {!viaFirestore ? (
                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between gap-2">
                    <h2 className="text-sm font-bold text-slate-900">Recent send log</h2>
                    <button
                      type="button"
                      onClick={loadSendLog}
                      disabled={logLoading}
                      className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    >
                      {logLoading ? "…" : "Refresh"}
                    </button>
                  </div>
                  {sendLog.length === 0 ? (
                    <p className="mt-3 text-xs text-slate-500">No sends logged yet.</p>
                  ) : (
                    <ul className="mt-3 max-h-48 space-y-2 overflow-y-auto text-xs">
                      {sendLog.map((row) => (
                        <li
                          key={row.id}
                          className={`rounded-lg border px-2 py-1.5 ${
                            row.ok ? "border-emerald-100 bg-emerald-50/50" : "border-rose-100 bg-rose-50/50"
                          }`}
                        >
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            <span className={`font-semibold ${row.ok ? "text-emerald-800" : "text-rose-800"}`}>
                              {row.ok ? "OK" : "Failed"}
                            </span>
                            <span className="text-slate-600">{row.kind || "email"}</span>
                            {row.orderId ? <span className="text-slate-500">order {String(row.orderId).slice(0, 8)}…</span> : null}
                          </div>
                          <p className="truncate text-slate-700">{row.to || "—"}</p>
                          <p className="truncate text-slate-500">{row.subject || row.error || ""}</p>
                          {row.at ? <p className="text-[10px] text-slate-400">{new Date(row.at).toLocaleString()}</p> : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ) : null}

              {/* Actions */}
              <section className="rounded-2xl border border-violet-200 bg-white p-5 shadow-sm">
                <h2 className="text-sm font-bold text-slate-900">Save & test</h2>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={save}
                    className="rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-violet-700 disabled:opacity-50"
                  >
                    {saving ? "Saving…" : "Save settings"}
                  </button>
                  <div className="flex min-w-0 flex-1 flex-col gap-1 sm:max-w-md">
                    <label className="text-xs font-semibold text-slate-600">Test recipient</label>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        className={inputCls}
                        placeholder={userEmail || "test@example.com"}
                        value={testTo}
                        onChange={(e) => setTestTo(e.target.value)}
                      />
                      {userEmail ? (
                        <button
                          type="button"
                          className="shrink-0 rounded-lg border border-slate-200 px-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                          onClick={() => setTestTo(userEmail)}
                        >
                          Me
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={testing || dirty}
                    onClick={sendTest}
                    className="rounded-xl border border-violet-300 bg-violet-50 px-5 py-2.5 text-sm font-semibold text-violet-900 hover:bg-violet-100 disabled:opacity-50"
                    title={dirty ? "Save settings first" : ""}
                  >
                    {testing ? "Sending…" : "Send test email"}
                  </button>
                </div>
              </section>
            </div>

            {/* Preview sidebar */}
            <aside className="lg:sticky lg:top-4 lg:self-start">
              <div className="mb-3 flex gap-1 rounded-lg border border-slate-200 bg-white p-1 text-[11px] font-semibold">
                {[
                  ["single", "1 file"],
                  ["plural", "2+ files"],
                  ["none", "No files"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setPreviewMode(id)}
                    className={`flex-1 rounded-md px-2 py-1.5 transition ${
                      previewMode === id ? "bg-violet-600 text-white" : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <EmailPreview cfg={cfg} previewMode={previewMode} />
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}
